import { LOCALE_ID, NgModule, InjectionToken, inject, provideAppInitializer } from '@angular/core';
import { AsyncPipe, registerLocaleData } from '@angular/common';
import { HttpClient, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDeCH from '@angular/common/locales/de-CH';
import { Router, RouterModule, Routes } from '@angular/router';
import moment from 'moment';
import 'moment/locale/de-ch';

// UI modules
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AuthenticationModule } from './authentication/authentication.module';

// Services
import { BusyService } from './services/busy.service';
import { CoreService } from './services/core.service';
import { DesktopService } from './services/desktop.service';
import { ModuleManagerService } from './services/module-manager.service';
import { SessionService } from './services/session.service';
import { CORE_ROUTER_MODULE, CORE_ROUTES } from './core-router.module';
import { HttpRequestCachingInterceptor } from './services/http-request-caching.interceptor';
import { DataSourceAvailabilityInterceptor } from './services/data-source-availability.interceptor';
import { HttpRequestCachingService } from './services/http-request-caching.service';
import { AscNotificationBarComponent } from './components/notification-bar/notification-bar.component';
import { NotificationBarService } from './services/notification-bar.service';
import { ClientConfiguration } from './model/client-configuraton';
import { FunctionClient } from './azure-functions';
import { NavigationService } from './services/navigation.service';
import { TelemetryService } from './services/telemetry.service';
import { AuthenticationService } from './authentication/services/authentication.service';
import { LoggingService } from './services/logging.service';

export const CLIENT_CONFIG = new InjectionToken<ClientConfiguration>('client.config');


@NgModule({
   declarations: [
      AscNotificationBarComponent
   ],
   exports: [
      RouterModule,
      AscNotificationBarComponent
   ],
   imports: [
      RouterModule,
      MatButtonModule,
      MatCardModule,
      MatIconModule,
      MatListModule,
      MatMenuModule,
      MatTooltipModule,
      AuthenticationModule,
      CORE_ROUTER_MODULE
   ],
   providers: [
      { provide: LOCALE_ID, useValue: 'de-CH' },

      /* MomentDateAdapter */
      { provide: MAT_DATE_LOCALE, useValue: 'de' },
      provideMomentDateAdapter(undefined, { useUtc: true, strict: true }),

      { provide: HTTP_INTERCEPTORS, useClass: HttpRequestCachingInterceptor, multi: true },
      { provide: HTTP_INTERCEPTORS, useClass: DataSourceAvailabilityInterceptor, multi: true },
      provideAppInitializer(async () => {

         // Dependency injection
         const httpClient = inject(HttpClient);
         const loggingService = inject(LoggingService);
         const telemetryService = inject(TelemetryService);
         const authenticationService = inject(AuthenticationService);
         const coreService = inject(CoreService);
         const moduleManagerService = inject(ModuleManagerService);
         const sessionService = inject(SessionService);
         const router = inject(Router);
         const routes = CORE_ROUTES;

         // Initialize function-controllers
         FunctionClient.httpClient = httpClient;

         // Load client configuration
         await coreService.loadClientConfigurationAsync();
         const clientConfiguration = coreService.clientConfiguration;

         // Initialize Application Insights
         telemetryService.config = {
            instrumentationKey: clientConfiguration.telemetry.applicationInsightsInstrumentationKey
         };
         telemetryService.init();

         // Initialize authentication module
         await AuthenticationModule.initializeAsync(clientConfiguration, authenticationService);

         // Initialize core module
         moduleManagerService.initialize(clientConfiguration);
         sessionService.initialize(clientConfiguration);

         // Sign-in
         console.group('[Core]', 'Signing-in');
         let isHandled = await authenticationService.loginAfterRedirectAsync();
         if (!isHandled) {
            isHandled = await authenticationService.loginWithUrlParametersAsync();
         }
         if (!isHandled) {
            isHandled = await authenticationService.loginSSOAsync();
         }
         loggingService.logInformation('[Core] %s', authenticationService.isAuthenticated ? 'Sign-in succeeded.' : 'Unable to sign-in, using Anonymous mode.');
         console.groupEnd();

         // Load modules
         console.group('[Core]', 'Loading modules');
         const userPrincipalName = authenticationService.user?.userPrincipalName;
         const moduleConfigurations = await moduleManagerService.loadModuleConfigurationAsync(userPrincipalName);
         await moduleManagerService.loadModulesAsync(moduleConfigurations);
         console.groupEnd();

         // Handle login after redirect for modules
         console.group('[Core]', 'Signing-in for modules');
         await authenticationService.loginAfterRedirectAsync();
         authenticationService.checkRedirectInteractionStatus();
         console.groupEnd();

         // Update routes for loaded modules
         const moduleIDs = moduleConfigurations.map(item => item.id);
         const config: Routes = moduleManagerService.getModuleRoutes(routes, moduleIDs);
         router.resetConfig(config);
         loggingService.logInformation('[Core] Navigation routes initialized');
      }),
      HttpRequestCachingService,
      AsyncPipe,
      BusyService,
      CoreService,
      DesktopService,
      LoggingService,
      ModuleManagerService,
      NavigationService,
      NotificationBarService,
      SessionService,
      TelemetryService,
      provideHttpClient(withInterceptorsFromDi())
   ]
})
export class CoreModule {
   constructor() {
      registerLocaleData(localeDeCH);
      moment.locale('de-ch');
   }
}
